import { useEffect, useState } from "react";
import { useRsnFeeValue } from "@directv-digital/dds.server.application-context";
import { getCookie, isValidRsnFee } from "@directv-digital/dds.services.cookie-service";
import { applyLiveRsnToAllIn, DpAllInData } from "lib/helper/dynamic-injection/all-in-rsn";

/**
 * Re-price a dynamic-pricing all-in string for the CUSTOMER'S zip (WAT-11203).
 *
 * The page-adapter paints `[<..OFFER<n>ALLIN>]` with the DEFAULT-zip RSN because the real
 * RSN is a client-only fact. This hook subscribes to the app-wide RSN context
 * (`useRsnFeeValue`, kept current by ZipcodeModule on every zip change) and re-derives the
 * string from `dpAllIn` — base + live RSN — via the shared `applyLiveRsnToAllIn`.
 *
 * MOUNT IT ON A LEAF, not on a card or a module. The subscription is what decides the
 * re-render blast radius: called from the smallest component that renders the text, a zip
 * change re-renders that one `DtvText` and nothing above it. That is the whole point — it
 * replaces both the module-wide `setCardData` re-render and the dead `data-dp-allin` DOM
 * marker (WAT-11586), which fusion's `DtvText` parser strips before it reaches the DOM.
 *
 * ONLY THE RESOLVED FEE IS STATE; THE STRING IS DERIVED DURING RENDER.
 *
 * The earlier version computed the string in an effect and stored it. That looked like it
 * preserved SSR fidelity, but the leaf is swapped for a `LoadingTextBlock` while
 * `isPricesLoading` is true, so on EVERY zip change `LiveAllInText` unmounts and its state
 * is lost. On remount it painted the server string first, the browser showed the OLD price,
 * and only then did the effect run and paint the right one — a visible flicker of an
 * incorrect price on regulated copy. Effect-derived state also went stale for a render
 * whenever `content` changed. (WAT-11203.)
 *
 * Deriving during render fixes both: whatever `content` and fee are current are the ones
 * used, on the very first paint after a remount. Hydration safety is preserved a different
 * way — the fee starts as `null` (nothing read from `window` during render), so the first
 * client render returns the server string byte-for-byte, exactly as SSR produced it. The
 * effect only publishes the fee. Mirrors the FlipCard sibling, which keeps the fee in state
 * and formats during render for the same reason.
 *
 * RENDER COST, stated honestly. A leaf with nothing to re-price (no `dpAllIn`, or no tiers —
 * every RSN-exempt card and every line without a dynamic all-in) publishes no fee and so
 * NEVER re-renders. A leaf that CAN change costs one extra render on mount, even at the
 * default zip, because the fee is published either way. The earlier version avoided that by
 * keeping the derived string in state, which is exactly what produced the wrong-price frame.
 * One cheap `DtvText` render is the right trade for never painting a wrong price.
 */
export const useLiveAllIn = (
  content: string | null | undefined,
  dpAllIn?: DpAllInData | null
): string => {
  // ASYMMETRY, deliberate — and it is PARITY with production, not a divergence.
  //
  // The SERVER half (`buildDpAllIn`) is per-PACKAGE: it reads that package's own
  // RSNFEE and validates every tier against it. This client half resolves ONE
  // app-wide fee (context, else the `rsnFee` cookie), seeded from the feed-global
  // `epochOffers.dtviOffersPrices.rsnFee`, and applies it to every tier of every card.
  //
  // That split is exactly what the LEGACY plan card already does today, in
  // `getCardsWithDynamicData` (plan-cards/utils/dynamicPrices.ts):
  //   useRsnFee = card?.pricingData?.useRsnFee ?? true   ← PRESENCE, per card
  //   totalPrice = subtotal + (useRsnFee ? rsnFeeNum : 0) ← AMOUNT, global
  //   "[rsn_fee]": rsnFeeNum.toFixed(2)                   ← AMOUNT, global
  // The per-package work on the server is the identification half — the analogue of
  // `useRsnFee` — which is why a package with UNKNOWN RSN (satellite-only MÁS LATINO /
  // MÁS ULTRA / LO MÁXIMO, which have no DTVI twin to reuse a fee from) returns null
  // from `buildDpAllIn` and never reaches this hook at all. An RSN-exempt package
  // (ENTERTAINMENT / ÓPTIMO MÁS, `rsnFeeDetails.visible === false`) never reaches this
  // hook either — `selectAttribute` (package-data.ts:172) already collapsed the exempt
  // `0` to `null`, so `rsn === null` in `buildDpAllIn` covers BOTH states.
  //
  // RSN amount is therefore zip-scoped but package-INVARIANT. Confirmed by the
  // feature owner on this PR: if EPOCH ever prices RSN per package, every RSN
  // surface in the app changes together (the global context value is app-wide), so
  // that is a deliberate architecture change and not a defect in this contract.
  // The server already computes the per-package amount, so threading it through
  // `DpAllInData` is the migration path when that day comes. (WAT-11203.)
  const contextRsnFee = useRsnFeeValue();
  // `null` = "no fee resolved on the client yet" → render the server string. This is what
  // keeps the first client render identical to SSR, since nothing browser-only is read
  // during render.
  const [clientRsnFee, setClientRsnFee] = useState<string | number | null>(null);

  useEffect(() => {
    // Nothing to re-price → never publish a fee, so this leaf NEVER re-renders. That is
    // most leaves: every RSN-exempt card and every line with no dynamic all-in. Cards that
    // CAN change pay one extra render on mount, which is the honest cost of deriving the
    // string during render instead of storing a stale one (see the docblock).
    if (!dpAllIn?.tiers?.length) {
      return;
    }
    // ⛔ Pass RAW values to `isValidRsnFee` — do NOT coalesce with `||`. The context
    // initializes to `undefined` until a zip resolves, so `undefined` means "not resolved
    // yet" while `0` means "resolved: this region has no RSN". `||` collapses both to
    // falsy, which in a 0-RSN market either left the default-zip price painted or matched a
    // stale cookie and early-returned — either way the regulated "w/ req'd fees" copy read
    // $13.99/mo too high, while the legacy engine on the same card computed it correctly
    // with 0. Same trap, same fix as the FlipCard sibling and zipcode-module.
    // (WAT-11203.)
    //
    // PRECEDENCE IS DELIBERATELY CONTEXT-FIRST, and differs from FlipCard, which prefers
    // the cookie. A zip the customer just entered should win immediately rather than wait
    // for the cookie write. The two therefore disagree for the short window before the
    // cookie is rewritten; that is accepted, not accidental.
    const cookieVal = getCookie("rsnFee");
    const resolved = isValidRsnFee(contextRsnFee) ?
      contextRsnFee :
      isValidRsnFee(cookieVal) ?
        cookieVal :
        null;
    setClientRsnFee(prev => (prev === resolved ? prev : resolved));
  }, [contextRsnFee, dpAllIn]);

  // Derived, not stored: correct on the first paint after a remount, and never stale
  // against `content`.
  return applyLiveRsnToAllIn(content, dpAllIn, clientRsnFee);
};

export default useLiveAllIn;
